/**
 * Exit Front Desk view dialog (DNKADM-0031).
 *
 * An MUI `Dialog` gating the return from Front Desk view to the full console
 * behind the signed-in admin's OWN password. Three actions:
 *  - **Submit** → `verifyPassword(password)` (dink-api `/me/verify-password`):
 *      - `valid`       → clear Front Desk view → full console;
 *      - `invalid`     → inline "Incorrect password, try again", stay, count a
 *                        failed attempt (a wrong password is `200 {valid:false}`,
 *                        NEVER a 401 — so it never trips the bearer
 *                        401→refresh→sign-out path);
 *      - `throttled`   → cool-down: disable submit + field, generic "too many tries";
 *      - `failClosed`  → FAIL CLOSED: stay locked, generic "couldn't verify right
 *                        now" (NOT a password error);
 *      - `sessionGone` → the JWT is gone → the Log out flow;
 *      - `blank`       → treated as a client validation error (the empty submit is
 *                        blocked client-side first, so this is belt-and-braces).
 *    The password field is cleared after EVERY submit; the password is never
 *    stored, cached, or logged.
 *  - **Cancel** → close, stay in Front Desk view.
 *  - **Log out** → the always-available escape (`authApi.logout()` → `signOut()` →
 *    `/auth/signed-out`) — works even before `/me/verify-password` is live.
 *
 * **Local UI lockout (required):** after {@link LOCKOUT_THRESHOLD} `invalid`
 * attempts the field is disabled locally for a cool-down — independent of the
 * backend `429` — so a kiosk user can't machine-gun attempts. The counter clears
 * on a successful exit.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { authApi } from '@/api/auth';
import { useAuth } from '@/auth/authHooks';
import { useFrontDeskView } from '@/auth/frontDeskHooks';
import { verifyPassword } from '@/api/verifyPassword';

/** Failed `valid:false` attempts that trip the local cool-down. */
const LOCKOUT_THRESHOLD = 5;
/** Local cool-down duration (ms) after the threshold, and the backend-`429` window. */
const LOCKOUT_COOLDOWN_MS = 30_000;

export interface ExitFrontDeskDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /**
   * Close the dialog. Called on Cancel (stay in Front Desk view) AND after a
   * successful exit (so the now-stale dialog unmounts as the full view reveals).
   */
  onClose: () => void;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  /** Inline "incorrect" after a wrong password. */
  | { kind: 'invalid' }
  /** Backend 429 cool-down OR the local N-attempt lockout — submit + field disabled. */
  | { kind: 'cooldown' }
  /** 500/502/malformed — fail closed; generic message, NOT a password error. */
  | { kind: 'failClosed' };

export function ExitFrontDeskDialog({ open, onClose }: ExitFrontDeskDialogProps) {
  // Remount the form per open so the field + attempt counter reseed cleanly and no
  // password ever lingers across opens.
  if (!open) return null;
  return <ExitFrontDeskForm onClose={onClose} />;
}

function ExitFrontDeskForm({ onClose }: { onClose: () => void }) {
  const ids = useId();
  const { signOut } = useAuth();
  const { exitFrontDeskView } = useFrontDeskView();

  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [loggingOut, setLoggingOut] = useState(false);
  const attemptsRef = useRef(0);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);

  // Focus the password field on open (without the a11y-discouraged `autoFocus` prop).
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);

  useEffect(
    () => () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    },
    [],
  );

  const startCooldown = useCallback(() => {
    setStatus({ kind: 'cooldown' });
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    cooldownTimer.current = setTimeout(() => {
      attemptsRef.current = 0;
      setStatus({ kind: 'idle' });
    }, LOCKOUT_COOLDOWN_MS);
  }, []);

  const inCooldown = status.kind === 'cooldown';
  const submitting = status.kind === 'submitting';
  const busy = submitting || loggingOut;
  const submitDisabled = busy || inCooldown || password.trim() === '';
  const fieldDisabled = busy || inCooldown;

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await authApi.logout();
    } catch {
      // A failed network logout must not block clearing the local session.
    }
    signOut();
  }, [signOut]);

  const handleSubmit = useCallback(async () => {
    if (password.trim() === '' || inCooldown || busy) return;
    setStatus({ kind: 'submitting' });
    const result = await verifyPassword(password);
    // Clear the field after EVERY submit — the password is never retained.
    setPassword('');

    switch (result.kind) {
      case 'valid':
        attemptsRef.current = 0;
        exitFrontDeskView();
        onClose();
        return;
      case 'invalid':
      case 'blank': {
        attemptsRef.current += 1;
        if (attemptsRef.current >= LOCKOUT_THRESHOLD) {
          startCooldown();
        } else {
          setStatus({ kind: 'invalid' });
        }
        return;
      }
      case 'throttled':
        startCooldown();
        return;
      case 'sessionGone':
        await handleLogout();
        return;
      case 'failClosed':
      default:
        setStatus({ kind: 'failClosed' });
        return;
    }
  }, [password, inCooldown, busy, exitFrontDeskView, startCooldown, handleLogout, onClose]);

  const titleId = `${ids}-title`;
  const passwordId = `${ids}-password`;

  return (
    <Dialog open onClose={busy ? undefined : onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>Exit Front Desk view</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DialogContentText sx={{ fontSize: 14 }}>
            Enter your password to switch back to the full console.
          </DialogContentText>

          <TextField
            id={passwordId}
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={fieldDisabled}
            inputRef={fieldRef}
            fullWidth
            size="small"
            error={status.kind === 'invalid'}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !submitDisabled) {
                e.preventDefault();
                void handleSubmit();
              }
            }}
            slotProps={{
              htmlInput: { 'aria-label': 'Password', autoComplete: 'current-password' },
            }}
          />

          {status.kind === 'invalid' && (
            <Alert severity="error" role="alert">
              Incorrect password, try again.
            </Alert>
          )}
          {status.kind === 'cooldown' && (
            <Alert severity="warning" role="alert">
              Too many tries — wait and try again shortly.
            </Alert>
          )}
          {status.kind === 'failClosed' && (
            <Alert severity="error" role="alert">
              Couldn&rsquo;t verify right now. Please try again in a moment.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => void handleLogout()} disabled={busy} color="inherit">
          Log out
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitDisabled}>
            {submitting ? 'Checking…' : 'Submit'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
